/**
 * useDrafts — draft state for the /add capture flow (ported from Noteble —
 * ADR-022; moved server-side by ADR-030 §9).
 *
 * Drafts are the pre-generation capture buffer. They used to live only in this
 * browser's IndexedDB, so an in-progress capture was lost on a device change.
 * They are now tenant-scoped, author-owned rows behind `captureBuffersApi`,
 * TTL-expired after 30 days.
 *
 * **Both stores are written, deliberately.** IndexedDB is not a cache to be
 * retired: it holds the photo *bytes*, which never go to the server (only the
 * mediaId does), and it is the only store that works with no network. So local
 * is the fast path and the offline path; the server is what survives a device
 * change.
 *
 * Neither store is authoritative, so a read RECONCILES them on `updatedAt`
 * (`newerDraftSource`) instead of preferring one. Preferring local
 * unconditionally is silent cross-device data loss: autosave PUTs whatever was
 * restored within 3 seconds, so restoring a stale local copy overwrites the
 * newer server row — the exact failure the server buffer was added to fix.
 *
 * Nothing here is ever indexed or retrievable: buffers are the `Captured`
 * state, and no assistant can reach one (see backend common/capture_buffer.py).
 */

import { useState, useCallback, useRef } from 'react';
import {
  indexedDBDraftsService,
  type LocalDraft,
  type LocalDraftNote,
  type LocalDraftPhoto,
  type DraftSummary,
} from '@/services/indexedDBDraftsService';
import {
  captureBuffersApi,
  type BufferDetail,
  type SaveBufferRequest,
} from '@/services/captureBuffersApi';
import { mediaApi } from '@/services/mediaApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('useDrafts');

export interface DraftItems {
  photos: LocalDraftPhoto[];
  notes: LocalDraftNote[];
  streamingTranscripts: Array<{ transcript: string; duration: number }>;
}

export interface RestoredDraft {
  inputMethod: string;
  languageCode: string;
  templateIds?: string[];
  photos: LocalDraftPhoto[];
  notes: LocalDraftNote[];
  streamingTranscripts: Array<{ transcript: string; duration: number }>;
}

function draftTitle(now: number): string {
  const dateStr = new Date(now);
  return `Draft — ${dateStr.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${dateStr.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * The server payload for a local draft. Photos are reduced to their mediaId —
 * one that hasn't uploaded yet has none and is dropped, which is why the local
 * copy stays authoritative for bytes.
 *
 * Exported for its own test: this function is the reason photo bytes never
 * leave the browser, and an accidental `...p` spread here would ship blobs to
 * DynamoDB without anything else noticing.
 */
export function toBufferRequest(draft: LocalDraft): SaveBufferRequest {
  return {
    title: draft.title,
    inputMethod: draft.inputMethod,
    languageCode: draft.languageCode,
    templateIds: draft.templateIds ?? [],
    items: {
      photos: draft.items.photos
        .filter((p) => !!p.mediaId)
        .map((p) => ({
          mediaId: p.mediaId!,
          timestamp: p.timestamp,
          ...(p.description ? { description: p.description } : {}),
          ...(p.mermaid ? { mermaid: p.mermaid } : {}),
        })),
      notes: draft.items.notes,
      streamingTranscripts: draft.items.streamingTranscripts,
    },
  };
}

/**
 * Server rows ∪ local-only rows, newest first.
 *
 * Server wins on conflict: those are the ones that survive a device change,
 * and after a migration pass the two agree anyway. Local-only rows are kept
 * rather than hidden — that is the fallback that makes an unreachable API a
 * degraded list instead of an empty one.
 */
export function mergeDraftLists(
  server: DraftSummary[],
  local: DraftSummary[],
): DraftSummary[] {
  const serverIds = new Set(server.map((d) => d.id));
  return [...server, ...local.filter((d) => !serverIds.has(d.id))]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/**
 * Which copy of a draft to restore — the newer one, by `updatedAt`.
 *
 * The two stores carry the timestamp in different shapes (local is epoch ms,
 * the server is ISO), which is half the reason the comparison was missing.
 *
 * Local wins a tie and wins an unparseable server timestamp: with nothing to
 * separate them, the copy holding the photo bytes is the cheaper and more
 * complete restore. Only a server row that is genuinely NEWER displaces it.
 */
export function newerDraftSource(
  local: { updatedAt: number } | null | undefined,
  server: { updatedAt: string } | null | undefined,
): 'local' | 'server' | null {
  if (!local) return server ? 'server' : null;
  if (!server) return 'local';
  const serverMs = Date.parse(server.updatedAt);
  if (Number.isNaN(serverMs)) return 'local';
  return serverMs > local.updatedAt ? 'server' : 'local';
}

/**
 * Re-download the bytes for a server buffer's photos.
 *
 * A cross-device restore has mediaIds and no blobs, but the bytes are durable
 * in the tenant media store — that is where they were uploaded at capture time.
 * Per-photo best effort: one unresolvable image drops that photo rather than
 * failing a restore that would otherwise return every note and transcript.
 *
 * `localPhotos` are the same-device blobs, when there are any. Restoring the
 * newer SERVER row must not cost bytes this browser already holds: a photo
 * whose mediaId is in IndexedDB is served from there, so choosing the server
 * copy stays a strict improvement over choosing local — the media round-trip
 * (and its per-photo failure mode) is skipped entirely.
 */
async function hydratePhotos(
  buffer: BufferDetail,
  localPhotos: LocalDraftPhoto[] = [],
): Promise<LocalDraftPhoto[]> {
  const localBlobs = new Map(
    localPhotos.filter((p) => p.mediaId).map((p) => [p.mediaId!, p.blob]),
  );
  const photos = await Promise.all(buffer.items.photos.map(async (p) => {
    try {
      const cached = localBlobs.get(p.mediaId);
      const blob = cached
        ?? await (await fetch((await mediaApi.getMedia(p.mediaId)).url)).blob();
      return {
        blob,
        timestamp: p.timestamp,
        mediaId: p.mediaId,
        ...(p.description ? { description: p.description } : {}),
        ...(p.mermaid ? { mermaid: p.mermaid } : {}),
      } as LocalDraftPhoto;
    } catch (err) {
      log.warn('Could not rehydrate a buffered photo', {
        mediaId: p.mediaId, error: String(err),
      });
      return null;
    }
  }));
  return photos.filter((p): p is LocalDraftPhoto => p !== null);
}

export function useDrafts() {
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  // Ids already pushed up this session. Migration is idempotent server-side
  // (PUT upsert), but re-uploading every local draft on every list refresh
  // would be pointless traffic.
  const migratedRef = useRef<Set<string>>(new Set());

  /**
   * Migrate local-only drafts to the server (ADR-030 §9).
   *
   * A draft captured before this shipped exists only in IndexedDB. Rather than
   * a one-shot migration that a user who never reopens the tab would miss,
   * every list refresh pushes up whatever the server does not have — so a
   * local draft is never silently lost, and it converges the first time the
   * user visits with a working connection.
   */
  const migrateLocalDrafts = useCallback(async (serverIds: Set<string>) => {
    const local = await indexedDBDraftsService.listDrafts();
    const pending = local.filter(
      (d) => !serverIds.has(d.id) && !migratedRef.current.has(d.id),
    );
    for (const summary of pending) {
      migratedRef.current.add(summary.id);
      try {
        const draft = await indexedDBDraftsService.getDraft(summary.id);
        if (!draft) continue;
        await captureBuffersApi.put(draft.draftId, toBufferRequest(draft));
        log.info('Local draft migrated to the server buffer', { draftId: draft.draftId });
      } catch (err) {
        // Leave it local and try again next load — the local copy is still
        // readable, so a failed migration costs nothing but portability.
        migratedRef.current.delete(summary.id);
        log.warn('Draft migration failed (staying local)', {
          draftId: summary.id, error: String(err),
        });
      }
    }
  }, []);

  const loadDrafts = useCallback(async () => {
    setIsLoading(true);
    try {
      let serverDrafts: DraftSummary[] = [];
      let serverReachable = true;
      try {
        serverDrafts = await captureBuffersApi.list();
      } catch (err) {
        // Offline or the API is down: fall back to local reads rather than
        // showing the user an empty drafts list they know is wrong.
        serverReachable = false;
        log.warn('Capture buffers unavailable — falling back to local drafts', {
          error: String(err),
        });
      }

      const serverIds = new Set(serverDrafts.map((d) => d.id));
      if (serverReachable) await migrateLocalDrafts(serverIds);

      const local = await indexedDBDraftsService.listDrafts();
      setDrafts(mergeDraftLists(serverDrafts, local));
      setHasAttemptedLoad(true);
    } catch (err) {
      log.error('Failed to load drafts', err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [migrateLocalDrafts]);

  /** Persist to both stores; the server write is best-effort. */
  const persist = useCallback(async (draft: LocalDraft): Promise<void> => {
    await indexedDBDraftsService.saveDraft(draft);
    try {
      await captureBuffersApi.put(draft.draftId, toBufferRequest(draft));
    } catch (err) {
      // The local write already succeeded, so the capture is not at risk on
      // this device — it just isn't portable yet. The next successful save or
      // the migration pass on next load carries it up.
      log.warn('Capture buffer save failed (draft kept locally)', {
        draftId: draft.draftId, error: String(err),
      });
    }
  }, []);

  const saveDraft = useCallback(async (
    items: DraftItems,
    inputMethod: string,
    languageCode: string,
    templateIds?: string[],
  ): Promise<string | null> => {
    setIsSaving(true);
    try {
      const now = Date.now();
      const draftId = `draft_${now}_${Math.random().toString(36).slice(2, 11)}`;

      const draft: LocalDraft = {
        draftId,
        title: draftTitle(now),
        inputMethod,
        languageCode,
        templateIds,
        createdAt: now,
        updatedAt: now,
        items,
      };

      await persist(draft);
      await loadDrafts();
      log.info('Draft saved', { draftId });
      return draftId;
    } catch (err) {
      log.error('Failed to save draft', err as Error);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [loadDrafts, persist]);

  /** Auto-save — upserts a draft with a stable ID. No UI state changes.
   *  Called on a 3s debounce by useAutoSaveDraft, never per keystroke. */
  const autoSaveDraft = useCallback(async (
    draftId: string,
    items: DraftItems,
    inputMethod: string,
    languageCode: string,
    templateIds?: string[],
  ): Promise<void> => {
    try {
      const existing = await indexedDBDraftsService.getDraft(draftId);
      const now = Date.now();

      const draft: LocalDraft = {
        draftId,
        title: draftTitle(now),
        inputMethod,
        languageCode,
        templateIds,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        items,
      };

      await persist(draft);
    } catch (err) {
      log.warn('Auto-save draft failed (silent)', { error: String(err) });
    }
  }, [persist]);

  const restoreDraft = useCallback(async (draftId: string): Promise<RestoredDraft> => {
    // Both stores are consulted, then reconciled on updatedAt. Reading local
    // alone is not an optimisation: this draft may have been edited on another
    // device since, and the autosave that follows a restore would push the
    // stale copy back over the newer row.
    const local = await indexedDBDraftsService.getDraft(draftId);

    let buffer: BufferDetail | null = null;
    try {
      buffer = await captureBuffersApi.get(draftId);
    } catch (err) {
      // Offline, or this draft never reached the server. A local copy still
      // restores; with neither store answering there is nothing to return, so
      // the error stands.
      if (!local) throw err;
      log.warn('Capture buffer unreadable — restoring the local draft', {
        draftId, error: String(err),
      });
    }

    if (local && newerDraftSource(local, buffer) === 'local') {
      log.info('Draft restored', {
        draftId,
        photos: local.items.photos.length,
        notes: local.items.notes.length,
      });
      return {
        inputMethod: local.inputMethod,
        languageCode: local.languageCode,
        templateIds: local.templateIds,
        photos: local.items.photos,
        notes: local.items.notes,
        streamingTranscripts: local.items.streamingTranscripts,
      };
    }

    if (!buffer) throw new Error(`Draft ${draftId} is in neither store`);

    // Either this device has never seen the draft, or another device has edited
    // it more recently — the case the server buffer exists for.
    const photos = await hydratePhotos(buffer, local?.items.photos);
    log.info('Draft restored from the server buffer', {
      draftId, photos: photos.length, notes: buffer.items.notes.length,
      supersededLocal: !!local,
    });
    return {
      inputMethod: buffer.inputMethod,
      languageCode: buffer.languageCode,
      templateIds: buffer.templateIds,
      photos,
      notes: buffer.items.notes,
      streamingTranscripts: buffer.items.streamingTranscripts,
    };
  }, []);

  /** Remove from both stores — a delete that only landed locally would come
   *  back the next time another device synced. */
  const forget = useCallback(async (draftId: string) => {
    await indexedDBDraftsService.deleteDraft(draftId);
    try {
      await captureBuffersApi.remove(draftId);
    } catch (err) {
      log.warn('Capture buffer delete failed', { draftId, error: String(err) });
    }
    setDrafts((prev) => prev.filter((d) => d.id !== draftId));
  }, []);

  const deleteDraft = useCallback(async (draftId: string) => {
    await forget(draftId);
    log.info('Draft deleted', { draftId });
  }, [forget]);

  /** Delete without logging — background cleanup and post-submission purge. */
  const silentDeleteDraft = useCallback(async (draftId: string) => {
    await forget(draftId);
  }, [forget]);

  return {
    drafts,
    isLoading,
    isSaving,
    hasAttemptedLoad,
    loadDrafts,
    saveDraft,
    autoSaveDraft,
    restoreDraft,
    deleteDraft,
    silentDeleteDraft,
    purgeAndForgetDraft: silentDeleteDraft,
  };
}
